import React from "react";
import { Box, useTheme } from "@mui/joy";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { InstructorStat } from "../../../api/stats/Stats.typed";
import { formatInstructorStatDisplayName, instructorStatKey } from "../instructorNameFormat";
import { colorTokens, textTokens } from "../../../styles/globalStyles";
import {
  statsChartAxisTick,
  statsChartTooltipLabelStyles,
  statsChartTooltipStyles,
} from "../statsChartStyles";

type InstructorChartRow = {
  label: string;
  fullLabel: string;
  dive_groups_count: number;
  dives_count: number;
};

function truncateLabel(s: string, maxChars: number): string {
  if (s.length <= maxChars) return s;
  return `${s.slice(0, maxChars - 1)}…`;
}

function aggregateInstructorStatsForChart(
  rows: InstructorStat[],
  maxLabelChars: number,
): InstructorChartRow[] {
  const byId = new Map<
    string,
    { fullLabel: string; dive_groups_count: number; dives_count: number }
  >();
  for (const r of rows) {
    const key = instructorStatKey(r);
    const prev = byId.get(key);
    if (prev) {
      prev.dive_groups_count += r.dive_groups_count;
      prev.dives_count += r.dives_count;
    } else {
      byId.set(key, {
        fullLabel: formatInstructorStatDisplayName(r),
        dive_groups_count: r.dive_groups_count,
        dives_count: r.dives_count,
      });
    }
  }
  return Array.from(byId.values())
    .map((v) => ({
      fullLabel: v.fullLabel,
      label: truncateLabel(v.fullLabel, maxLabelChars),
      dive_groups_count: v.dive_groups_count,
      dives_count: v.dives_count,
    }))
    .sort((a, b) => b.dives_count + b.dive_groups_count - (a.dives_count + a.dive_groups_count));
}

type InstructorStatsBarChartProps = {
  rows: InstructorStat[];
};

export default function InstructorStatsBarChart(props: InstructorStatsBarChartProps) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  const yAxisWidth = isXs ? 128 : isMdDown ? 176 : 220;
  const maxLabelChars = isXs ? 22 : isMdDown ? 36 : 46;
  const axisTick = React.useMemo(
    () => ({
      ...statsChartAxisTick,
      fontSize: isXs ? 11 : 12,
    }),
    [isXs],
  );

  const chartData = React.useMemo(
    () => aggregateInstructorStatsForChart(props.rows, maxLabelChars),
    [props.rows, maxLabelChars],
  );

  const legendBottomMargin = isXs ? 72 : 60;
  const extraChartHeightForLegend = legendBottomMargin - 16;

  const chartHeight = React.useMemo(() => {
    const perBar = 52;
    const base = 152 + extraChartHeightForLegend;
    return Math.max(300 + extraChartHeightForLegend, base + chartData.length * perBar);
  }, [chartData.length, extraChartHeightForLegend]);

  if (chartData.length === 0) return null;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflow: "visible",
      }}
    >
      <Box sx={{ width: "100%", height: chartHeight, minHeight: chartHeight, maxWidth: "100%" }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout="vertical"
            data={chartData}
            margin={{ top: 12, right: 12, left: 8, bottom: legendBottomMargin }}
            barCategoryGap={12}
            barGap={4}
          >
            <CartesianGrid
              strokeDasharray="3 6"
              stroke={colorTokens.border.default}
              horizontal={false}
            />
            <XAxis
              type="number"
              tick={axisTick}
              axisLine={{ stroke: colorTokens.border.default }}
              tickLine={{ stroke: colorTokens.border.default }}
            />
            <YAxis
              type="category"
              dataKey="label"
              width={yAxisWidth}
              tick={axisTick}
              axisLine={{ stroke: colorTokens.border.default }}
              tickLine={false}
              interval={0}
            />
            <Tooltip
              cursor={{ fill: colorTokens.bg.hover, opacity: 0.35 }}
              contentStyle={statsChartTooltipStyles}
              labelStyle={statsChartTooltipLabelStyles}
              formatter={(value: number, name: string) => [
                value,
                name === "dive_groups_count" ? "Groupes" : "Clients (non uniques)",
              ]}
              labelFormatter={(_, payload) => {
                const row = payload?.[0]?.payload as InstructorChartRow | undefined;
                return row?.fullLabel ?? "";
              }}
            />
            <Legend
              layout="vertical"
              verticalAlign="bottom"
              align="center"
              wrapperStyle={{
                width: "100%",
                left: 0,
                right: 0,
                paddingTop: 10,
                paddingLeft: 8,
                paddingRight: 8,
                boxSizing: "border-box",
                color: colorTokens.text.secondary,
                fontFamily: textTokens.fontFamily,
                fontSize: isXs ? 11 : 12,
                lineHeight: 1.4,
              }}
              formatter={(value) =>
                value === "dive_groups_count"
                  ? "Groupes"
                  : value === "dives_count"
                    ? "Clients (non uniques)"
                    : value
              }
            />
            <Bar
              dataKey="dive_groups_count"
              name="dive_groups_count"
              fill={colorTokens.accent.primary}
              radius={[0, 4, 4, 0]}
              maxBarSize={28}
            />
            <Bar
              dataKey="dives_count"
              name="dives_count"
              fill={colorTokens.text.title}
              radius={[0, 4, 4, 0]}
              maxBarSize={28}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
